import { spawn, execSync, type ChildProcess } from 'child_process';

interface SessionOptions {
  systemPrompt: string;
  dangerouslySkipPermissions?: boolean;
  resume?: boolean;
  sessionId?: string;
  model?: string;
}

interface BattleAgentOptions {
  systemPrompt: string;
  prompt: string;
  dangerouslySkipPermissions?: boolean;
  model?: string;
  effort?: string;
}

export class ClaudeBridge {
  private claudePath: string;

  constructor(claudePath?: string) {
    this.claudePath = claudePath ?? this.findClaudePath();
  }

  findClaudePath(): string {
    try {
      return execSync('which claude', { encoding: 'utf-8' }).trim();
    } catch {
      return 'claude';
    }
  }

  buildSessionArgs(options: SessionOptions): string[] {
    const args: string[] = [];

    if (options.dangerouslySkipPermissions) {
      args.push('--dangerously-skip-permissions');
    }

    args.push('--append-system-prompt', options.systemPrompt);

    if (options.resume && options.sessionId) {
      args.push('--resume', options.sessionId);
    } else if (options.resume) {
      args.push('--resume');
    }

    if (options.model) {
      args.push('--model', options.model);
    }

    return args;
  }

  buildBattleArgs(options: BattleAgentOptions): string[] {
    const args: string[] = ['--print'];

    if (options.dangerouslySkipPermissions) {
      args.push('--dangerously-skip-permissions');
    }

    if (options.model) {
      args.push('--model', options.model);
    }

    if (options.effort) {
      args.push('--effort', options.effort);
    }

    // System prompt is embedded in stdin via buildBattleStdin to avoid
    // CLI argument length issues that cause Claude to hang
    return args;
  }

  buildBattleStdin(options: BattleAgentOptions): string {
    return `<SYSTEM_PROMPT>\n${options.systemPrompt}\n</SYSTEM_PROMPT>\n\n${options.prompt}`;
  }

  spawnInteractive(args: string[]): ChildProcess {
    return spawn(this.claudePath, args, {
      stdio: 'inherit',
      env: { ...process.env },
    });
  }

  spawnNonInteractive(args: string[], cwd?: string): ChildProcess {
    return spawn(this.claudePath, args, {
      stdio: ['pipe', 'pipe', 'pipe'],
      env: { ...process.env },
      ...(cwd ? { cwd } : {}),
    });
  }

  async runAndCapture(args: string[], timeout: number = 300_000, stdinData?: string, cwd?: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const child = this.spawnNonInteractive(args, cwd);
      let stdout = '';
      let stderr = '';
      let killed = false;

      if (stdinData) {
        child.stdin?.write(stdinData);
        child.stdin?.end();
      }

      const timer = setTimeout(() => {
        killed = true;
        child.kill('SIGTERM');
      }, timeout);

      child.stdout?.on('data', (data) => { stdout += data.toString(); });
      child.stderr?.on('data', (data) => { stderr += data.toString(); });

      child.on('close', (code) => {
        clearTimeout(timer);
        if (killed) {
          reject(new Error(`Claude process timed out after ${timeout}ms`));
        } else if (code === 0) {
          resolve(stdout);
        } else {
          reject(new Error(`Claude exited with code ${code}: ${stderr}`));
        }
      });

      child.on('error', (err) => {
        clearTimeout(timer);
        reject(err);
      });
    });
  }
}
